// Ultimate Card Game (UCG) - Bot AI perception
//
// What the bot knows about the board, in one place. Before this module the
// same unit scans lived three times over: BotAI.analyzeBattlefieldThreats(),
// WavePlanner.ownHalfBalance() / its territory loop and NukeController.assess(),
// each with its own copy of the "undetected cloaked enemies are invisible"
// rule. Now:
//
//   BoardView   built once per decision tick (BotAI.makeCardDecision); the
//               tactical value scan is lazy, so a tick that never asks pays
//               nothing, and one scan serves the nuke controller and the
//               wave planner alike. The board does not change between
//               nuke.tick(), waves.tick() and analyzeBattlefieldThreats()
//               (all run before the tick's first play), so sharing the scan
//               is exactly equivalent to scanning three times.
//   Belief      what the bot may KNOW about the opponent beyond the board:
//               plasma and hand. All reads go through here, behind the
//               `omniscience` flags of the difficulty profile
//               (js/engine/ai/profiles.js). Every tier currently reads the
//               true values (design decision E5 in
//               docs/AI_MASTERPLAN_2026-10.md: hidden information advantages
//               stay as they are), but the switch to fair play is now one
//               profile field per advantage instead of a hunt through
//               four modules.
//
// Pure refactor of existing behaviour (Phase 0.4): tests/ai_replay_golden_test.mjs
// pins the bot's decisions bit for bit.

import { BALANCE } from '../../data/balance.js';
import { aiAdaptiveMemory } from './adaptiveMemory.js';
import { OMNISCIENCE_ALL } from './profiles.js';

// A bot (or a test mock) without a profile keeps today's behaviour.
export function omniscienceOf(bot) {
  return bot?.profile?.omniscience ?? OMNISCIENCE_ALL;
}

export function enemyTeamOf(team) {
  return team === 'western' ? 'eastern' : 'western';
}

// Distance from the bot's own base within which enemy value counts as an
// immediate threat (the old heuristic's x > 1020 / x < 580 zone).
export const NEAR_BASE_PX = 520;

// Computes a unit's effective tactical value for superweapon and wave
// assessment:
// - Accounts for 3-card Hyper/Melt fusion investment (Melt cards store base cost in cardData)
// - Discounts heavily damaged units (< 70% HP) so the bot doesn't waste a nuke on dying units
export function computeUnitTacticalValue(u) {
  let cost = u.cardData?.cost || 6;
  const isMeltOrHyper = Boolean(
    u.isHyper ||
    u.isMelted ||
    u.cardData?.type === 'Melt' ||
    (u.name && u.name.startsWith('Hyper ')) ||
    (u.cardData?.name && u.cardData.name.startsWith('Hyper '))
  );
  if (isMeltOrHyper) {
    cost *= 2.5;
  }
  const maxHp = u.maxHp || u.cardData?.hp || u.hp || 0;
  if (maxHp > 0 && typeof u.hp === 'number' && u.hp < maxHp) {
    const hpRatio = Math.max(0, Math.min(1, u.hp / maxHp));
    if (hpRatio < 0.7) {
      cost *= (0.25 + 0.75 * (hpRatio / 0.7));
    }
  }
  return cost;
}

// One pass over the board, valued by computeUnitTacticalValue(): the figures
// the nuke controller (swing, danger, parachutes) and the wave planner (own
// half balance, territory contest) both read. Only what the bot may know
// counts: undetected cloaked enemies are invisible to it, and golden statues
// (Looting Vulture) are skipped because a wipe cannot destroy them. Every
// accumulator adds in unit order, so each sum is the very float the separate
// scans produced.
export function scanTactical(bot) {
  const { battlefield, team } = bot;
  const enemy = enemyTeamOf(team);
  const ownBase = battlefield.bases?.[team];
  const midline = battlefield.width / 2;
  const inOwnHalf = team === 'eastern' ? (x) => x > midline : (x) => x < midline;

  let enemyValue = 0;
  let ownValue = 0;
  let enemyInOwnHalf = 0;
  let ownInOwnHalf = 0;
  let enemyNearBase = 0;
  let ownNearBase = 0;
  let enemyParachutingCount = 0;
  let enemyParachutingValue = 0;
  const visibleEnemies = [];

  for (const u of battlefield.units) {
    if (u.isDead || u.isGoldenStatue || u.isCinematic || u.indestructible) continue;
    const cost = computeUnitTacticalValue(u);
    const nearBase = ownBase ? Math.abs(u.x - ownBase.x) < NEAR_BASE_PX : false;
    const ownHalf = inOwnHalf(u.x);
    if (u.team === enemy) {
      if (u.isCloaked && !battlefield.isPositionDetected?.(team, u.x, u.y)) continue;
      if (u.isParachuting) {
        enemyParachutingCount++;
        enemyParachutingValue += cost;
      }
      enemyValue += cost;
      visibleEnemies.push(u);
      if (ownHalf) enemyInOwnHalf += cost;
      if (nearBase) enemyNearBase += cost;
    } else if (u.team === team) {
      ownValue += cost;
      if (ownHalf) ownInOwnHalf += cost;
      if (nearBase) ownNearBase += cost;
    }
  }

  return {
    enemyValue,
    ownValue,
    enemyInOwnHalf,
    ownInOwnHalf,
    enemyNearBase,
    ownNearBase,
    enemyParachutingCount,
    enemyParachutingValue,
    visibleEnemies
  };
}

// The bot's picture of the board for ONE decision tick. Build it at the top
// of the tick; read `tactical` as often as needed.
export class BoardView {
  constructor(bot) {
    this.bot = bot;
    this._tactical = null;
  }

  get tactical() {
    return this._tactical || (this._tactical = scanTactical(this.bot));
  }
}

// What the bot may know about the opponent beyond the board. See the header
// for the omniscience flags; the estimate below only runs for a tier that
// switches `enemyPlasma` off, which none does today.
export class Belief {
  constructor(bot) {
    this.bot = bot;
    this.reset();
  }

  reset() {
    this.seenUnitIds = new Set();
    this.seenSpend = 0;
  }

  // The opponent's plasma. Returns the raw value (undefined when the economy
  // has no slot for them), so every caller keeps its own default.
  enemyPlasma() {
    const { economy, team } = this.bot;
    if (omniscienceOf(this.bot).enemyPlasma) return economy.resources[enemyTeamOf(team)]?.plasma;
    return this.estimateEnemyPlasma();
  }

  // A human's mental ledger: what the opponent has earned by now, minus what
  // the bot has watched them spend. Spells and modules leave no unit on the
  // board and go uncounted, so this runs high; it exists to give the fair
  // setting a defined, monotone answer, not to be precise.
  estimateEnemyPlasma() {
    const earned = BALANCE.startPlasma + BALANCE.plasmaRate * (this.bot.gameTime || 0);
    return Math.max(0, Math.min(BALANCE.maxPlasma, earned - this.seenSpend));
  }

  // Called for every visible enemy unit while the estimate is in use: a unit
  // seen for the first time cost its card plus the draw that fetched it.
  observe(unit) {
    const id = unit.id ?? unit.instanceId;
    if (id === undefined || this.seenUnitIds.has(id)) return;
    this.seenUnitIds.add(id);
    const drawCost = this.bot.cardSystem?.drawCost ?? BALANCE.drawCost;
    this.seenSpend += (unit.cardData?.cost || 0) + drawCost;
  }

  // The opponent's hand as far as the bot may read it. Unknown = empty,
  // which makes a hand-reading card (Mental Extraction) a card to skip.
  enemyHand() {
    const { cardSystem, team } = this.bot;
    if (!omniscienceOf(this.bot).enemyHand) return [];
    return cardSystem?.hands?.[enemyTeamOf(team)] || [];
  }
}

// Analyzes living units on the battlefield to compute the tactical threat
// profile the hand scorer reads (BotAI.analyzeBattlefieldThreats() delegates
// here). Besides the scan it keeps the bot's rolling enemy memory current.
export function scanThreats(bot, enemyTeam) {
  const ownBase = bot.battlefield.bases ? bot.battlefield.bases[bot.team] : null;
  if (!bot._scratchEnemyUnits) bot._scratchEnemyUnits = [];
  if (!bot._scratchOwnUnits) bot._scratchOwnUnits = [];
  const enemyUnits = bot._scratchEnemyUnits;
  const ownUnits = bot._scratchOwnUnits;
  enemyUnits.length = 0;
  ownUnits.length = 0;
  let enemyAirUnits = 0;
  let enemyArmoredUnits = 0;
  let enemyInfantryCount = 0;
  let enemyGroundCount = 0;
  let enemyNearBaseCount = 0;
  let ownAirTargeters = 0;

  const units = bot.battlefield?.units || [];
  const myTeam = bot.team;
  const isEastern = myTeam === 'eastern';
  const omni = omniscienceOf(bot);
  // cloakLeak: the audio flags betray an Anti Shade the bot has not detected.
  let enemyHasCloakedThreats = omni.cloakLeak && !!(bot.audio && (bot.audio._antiShadeSource || bot.audio.isAntiShadeBreathPlaying));

  let enemyStructuresCount = 0;
  let enemyArtilleryCount = 0;
  let enemyParachutingCount = 0;
  let enemyLethalGlassCannons = 0;
  let enemyHackerCount = 0;
  let ownHackerCount = 0;
  let ownDisabledByHackerCount = 0;
  let ownDisabledHeavyByHackerCount = 0;

  for (let i = 0; i < units.length; i++) {
    const u = units[i];
    if (u.isDead) continue;
    if (u.team === enemyTeam) {
      if (u.isParachuting) {
        enemyParachutingCount++;
      }
      if ((omni.cloakLeak && u.isAntiShade) || (u.isCloaked && bot.battlefield.isPositionDetected && bot.battlefield.isPositionDetected(myTeam, u.x, u.y))) {
        enemyHasCloakedThreats = true;
      }
      // The bot only reacts to what a player could see: an undetected
      // cloaked unit (Shade) is invisible to it as well.
      if (u.isCloaked && bot.battlefield.isPositionDetected && !bot.battlefield.isPositionDetected(myTeam, u.x, u.y)) continue;
      enemyUnits.push(u);
      if (!omni.enemyPlasma) bot.belief?.observe(u);

      // Live In-Match Profiling (Match 1 early adaptation): record visible enemy units
      const isHacker = Boolean(
        (u.isHacker || u.unitType === 'Hacker' || u.cardData?.name === 'Hacker' || u.cardName === 'Hacker' || u.name === 'Hacker') &&
        !u.isDead && !u.isTranscendent && u.hackerState !== 'EXHAUSTED'
      );
      if (isHacker) {
        enemyHackerCount++;
        if (bot.enemyMemory) bot.enemyMemory.recentHackerTime = bot.gameTime;
      }
      if (bot.enemyMemory && bot.difficulty !== 'easy') {
        const uid = u.id || u.instanceId;
        const cName = u.cardData?.name || u.cardName || u.name;
        if (cName && (!uid || !bot.enemyMemory.observedUnitIds?.has(uid))) {
          if (uid && bot.enemyMemory.observedUnitIds) bot.enemyMemory.observedUnitIds.add(uid);
          if (!bot.enemyMemory.observedCardNames) bot.enemyMemory.observedCardNames = [];
          bot.enemyMemory.observedCardNames.push(cName);
          bot._observeEnemyPlay?.(cName);
          if (bot.enemyMemory.observedCardNames.length >= 3 || u.isCloaked || u.isAntiShade || isHacker || cName === 'Shade' || cName === 'Orchestra' || cName === 'Hacker') {
            bot.liveOpponentProfile = aiAdaptiveMemory.analyzeDeckThreats(bot.enemyMemory.observedCardNames);
          }
        }
      }
      if (u.isAir) {
        enemyAirUnits++;
        if (bot.enemyMemory) bot.enemyMemory.recentAirTime = bot.gameTime;
      } else {
        enemyGroundCount++;
      }
      const uType = u.cardData?.unitType || u.unitType;
      const isInfantry = (
        uType === 'Marine' || uType === 'HyperMarine' ||
        u.attributes?.includes('Biological') ||
        u.attributes?.includes('Light') ||
        (!u.isAir && !u.isStructure && (u.cardData?.cost || 10) <= 14 && (u.hp || 50) <= 120)
      );
      if (isInfantry) {
        enemyInfantryCount++;
      }
      const isArmored = (
        (u.deflectionRating && u.deflectionRating >= 0.20) ||
        u.attributes?.includes('Mechanical') ||
        u.attributes?.includes('Massive') ||
        (u.hp && u.hp >= 150) ||
        u.isStructure
      );
      if (isArmored) {
        enemyArmoredUnits++;
        if (bot.enemyMemory) bot.enemyMemory.recentHeavyArmorTime = bot.gameTime;
      }
      const dps = (u.damage || 0) / Math.max(0.25, u.attackSpeed || 1);
      if (((u.damage || 0) >= 50 || dps >= 20) && (u.hp || 10) <= 60 && !u.isStructure) {
        enemyLethalGlassCannons++;
      }
      if (u.isStructure || u.cardData?.isStructure || uType === 'MonitorLock' || uType === 'DeployableCannon') {
        enemyStructuresCount++;
      }
      if (u.cardData?.name === 'Orchestra' || u.name === 'Orchestra' || (u.range && u.range >= 40)) {
        enemyArtilleryCount++;
      }
      const isThreat = isEastern ? (u.x > 1000) : (u.x < 600);
      if (isThreat) enemyNearBaseCount++;
    } else if (u.team === myTeam) {
      ownUnits.push(u);
      if (u.targetsAir !== false && (u.projectileType || u.damage > 0)) {
        ownAirTargeters++;
      }
      if ((u.isHacker || u.unitType === 'Hacker' || u.cardData?.name === 'Hacker' || u.name === 'Hacker') && !u.isDead && !u.isTranscendent && u.hackerState !== 'EXHAUSTED') {
        ownHackerCount++;
      }
      if (u.isDisabled && (u.disabledByHackerId || u.isHacked)) {
        ownDisabledByHackerCount++;
        const cost = u.cardData?.cost || 10;
        if (cost >= 18 || (u.hp || 0) >= 150) {
          ownDisabledHeavyByHackerCount++;
        }
      }
    }
  }

  if (enemyHasCloakedThreats && bot.enemyMemory) {
    bot.enemyMemory.recentCloakTime = bot.gameTime;
  }

  const midline = bot.battlefield.width / 2;
  const divider = (bot.battlefield.territoryDivider !== undefined && bot.battlefield.territoryDivider !== null)
    ? bot.battlefield.territoryDivider
    : midline;
  const isPressedBack = bot.team === 'eastern' ? (divider > midline + 40) : (divider < midline - 40);

  const recentAir = (bot.gameTime - (bot.enemyMemory?.recentAirTime ?? -999)) < 25;
  const recentCloak = (bot.gameTime - (bot.enemyMemory?.recentCloakTime ?? -999)) < 30;
  const recentArmor = (bot.gameTime - (bot.enemyMemory?.recentHeavyArmorTime ?? -999)) < 25;
  const recentHacker = (bot.gameTime - (bot.enemyMemory?.recentHackerTime ?? -999)) < 25;
  const hasEnemySiegeLine = (enemyArtilleryCount > 0 && enemyStructuresCount > 0) || (enemyStructuresCount >= 2);

  return {
    enemyUnits,
    ownUnits,
    ownBase,
    enemyAirUnits,
    ownAirTargeters,
    enemyArmoredUnits,
    enemyInfantryCount,
    enemyGroundCount,
    enemyParachutingCount,
    hasEnemyParachuting: enemyParachutingCount > 0,
    enemyStructuresCount,
    enemyArtilleryCount,
    hasEnemySiegeLine,
    enemyNearBaseCount,
    isPressedBack,
    enemyHasCloakedThreats,
    enemyLethalGlassCannons,
    enemyHackerCount,
    enemyHasHacker: enemyHackerCount > 0,
    ownHackerCount,
    ownDisabledByHackerCount,
    ownDisabledHeavyByHackerCount,
    recentAir,
    recentCloak,
    recentArmor,
    recentHacker
  };
}
